import { type MaybeRefOrGetter, type Ref, computed, ref, toValue, watch } from 'vue';

import { useFFRestApiWithOptions } from '@/composables/useApi';
import { shouldRetryTransientFailure } from '@/composables/useApi.util';
import { ErrorService } from '@/core/shared/errors/error.service';
import type { BeforeFetchContext } from '@vueuse/core';

export interface SignedResumeUrl {
  signedUrl: string;
  expiresAt: string;
}

/**
 * A server-owned resume signer. Unlike the legacy storage signer, its key identifies a
 * candidate and never carries an object path from the browser.
 */
export interface ResumeUrlSigner {
  /** Includes the authorization scope as well as the candidate id (for example, employer:id). */
  identity: MaybeRefOrGetter<string | null | undefined>;
  candidateId: MaybeRefOrGetter<number | null | undefined>;
  sign: (candidateId: number) => Promise<SignedResumeUrl>;
}

export interface UseSignedResumeUrlReturn {
  signedUrl: Ref<string | null>;
  isLoading: Ref<boolean>;
  error: Ref<unknown>;
}

// Signed-url mint is side-effect-free, so retry once on transient 5xx.
const useSignedResumeUrlApi = useFFRestApiWithOptions({
  retry: 1,
  retryIf: shouldRetryTransientFailure,
});

export const useSignedResumeUrl = (
  source: MaybeRefOrGetter<string | null | undefined> = undefined,
  signer?: ResumeUrlSigner,
  active?: MaybeRefOrGetter<boolean>,
): UseSignedResumeUrlReturn => {
  if (signer) {
    return useServerOwnedSignedResumeUrl(signer, active);
  }

  const payload = computed(() => ({ url: toValue(source) ?? '' }));

  const { data, error, isFetching, onFetchError } = useSignedResumeUrlApi('/storage/signed-url', {
    refetch: true,
    beforeFetch: (ctx: BeforeFetchContext) => {
      if (!toValue(source)) ctx.cancel();
    },
  })
    .post(payload)
    .json<SignedResumeUrl>();

  onFetchError((err) => ErrorService.captureException(err));

  const signedUrl = computed(() => data.value?.signedUrl ?? null);

  return { signedUrl, isLoading: isFetching, error };
};

const refreshBufferMs = 60_000;

function hasUsableCachedUrl(response: SignedResumeUrl): boolean {
  const expiresAt = Date.parse(response.expiresAt);
  return Number.isFinite(expiresAt) && expiresAt > Date.now() + refreshBufferMs;
}

function useServerOwnedSignedResumeUrl(
  signer: ResumeUrlSigner,
  active: MaybeRefOrGetter<boolean> = true,
): UseSignedResumeUrlReturn {
  const signedUrl = ref<string | null>(null);
  const isLoading = ref(false);
  const error = ref<unknown>(null);
  // Keep this cache inside one viewer instance so a signed URL cannot survive an
  // employer/session change, while candidate switches in that viewer remain efficient.
  const signedUrlCache = new Map<string, SignedResumeUrl>();
  let requestVersion = 0;

  watch(
    () => [toValue(signer.identity), toValue(signer.candidateId), toValue(active)] as const,
    async ([identity, candidateId, isActive]) => {
      const request = ++requestVersion;
      signedUrl.value = null;
      error.value = null;

      if (!isActive || !identity || !candidateId) {
        isLoading.value = false;
        return;
      }

      const cached = signedUrlCache.get(identity);
      if (cached && hasUsableCachedUrl(cached)) {
        signedUrl.value = cached.signedUrl;
        isLoading.value = false;
        return;
      }

      isLoading.value = true;
      try {
        const response = await signer.sign(candidateId);
        if (request !== requestVersion) return;
        signedUrlCache.set(identity, response);
        signedUrl.value = response.signedUrl;
      } catch (requestError) {
        if (request !== requestVersion) return;
        error.value = requestError;
        ErrorService.captureException(requestError);
      } finally {
        if (request === requestVersion) isLoading.value = false;
      }
    },
    { immediate: true },
  );

  return { signedUrl, isLoading, error };
}
